import React from 'react'
import numeral from 'numeral'
import { Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react'
import { Box, Flex, Spacer } from '@chakra-ui/layout'
import { Doughnut } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import type {
  AnalyticsAPIResponse,
  StatusAndErrorCodeDistributionPieChartData,
  ObjectOfNameAndCount,
  ObjectOfBandwidthCountAndName,
} from '../../interfaces'
import DoughnutColuredLabel from './DoughnutColuredLabel'

const pieBackgrounds = ['#27961d', '#ad2d2d', '#70156d']

const consolidateCacheData = (
  cacheData: ObjectOfBandwidthCountAndName[],
): ObjectOfNameAndCount[] => {
  const newCacheData: Array<{
    name: 'Hit' | 'Miss' | 'Error'
    count: number
  }> = []
  let hitFlag = false
  let errorFlag = false
  let hitCount = 0
  let errorCount = 0
  let refreshHitCount = 0
  let capacityCount = 0

  for (const object of cacheData) {
    if (object.name === 'RefreshHit') {
      refreshHitCount = Number(object.count)
    }
    if (object.name === 'CapacityExceeded') {
      capacityCount = Number(object.count)
    }
    if (object.name === 'Miss') {
      newCacheData.push({
        name: 'Miss',
        count: Number(object.count),
      })
    }
  }

  for (const object of cacheData) {
    if (object.name === 'Hit') {
      hitCount = Number(object.count)
      hitCount += refreshHitCount
      hitFlag = true
    }
    if (object.name === 'Error') {
      errorCount = Number(object.count)
      errorCount += capacityCount
      errorFlag = true
    }
  }

  if (!hitFlag && refreshHitCount !== 0) {
    hitCount = refreshHitCount
  }
  if (!errorFlag && capacityCount !== 0) {
    errorCount = capacityCount
  }

  newCacheData.push({
    name: 'Error',
    count: Number(errorCount),
  })

  newCacheData.unshift({
    name: 'Hit',
    count: Number(hitCount),
  })
  return newCacheData
}

const getPieChartData = (
  data: ObjectOfNameAndCount[],
): StatusAndErrorCodeDistributionPieChartData | undefined => {
  if (!data.length) {
    return
  }

  let total = 0
  data.forEach(element => {
    total += Number(element.count)
  })

  const labels: string[] = []
  const dataset: number[] = []
  for (const cacheHitObj of data) {
    if (cacheHitObj.count) {
      const percentage = parseFloat(
        ((cacheHitObj.count / total) * 100).toFixed(2),
      )

      dataset.push(percentage)
      labels.push(cacheHitObj.name)
    }
  }

  return {
    data: {
      datasets: [
        {
          backgroundColor: pieBackgrounds,
          data: dataset,
        },
      ],
      labels,
    },
    countData: data,
    options: {
      responsive: true,
      radius: 125,
      plugins: {
        legend: {
          display: false,
        },
      },
    },
  }
}

interface Props {
  data: AnalyticsAPIResponse
}

const CacheHitRatio = ({ data }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const cacheData = data.resultType

  if (!cacheData || !Object.keys(cacheData).length) {
    return (
      <Box marginTop="1rem">{t('no_data_available', 'No Data Available')}</Box>
    )
  }

  const updatedCacheData = consolidateCacheData(cacheData)

  const pieChartData: StatusAndErrorCodeDistributionPieChartData | undefined =
    getPieChartData(updatedCacheData)

  const tableRows: React.ReactElement[] = []

  if (pieChartData) {
    for (let i = 0; i < pieChartData.data.labels.length; i++) {
      tableRows.push(
        <Tr key={pieChartData.data.labels[i]}>
          <Td>
            <DoughnutColuredLabel
              label={pieChartData.data.labels[i]}
              color={pieChartData.data.datasets[0].backgroundColor[i]}
            />
          </Td>
          <Td>{`${pieChartData.data.datasets[0].data[i]} %`}</Td>
          <Td>
            {numeral(pieChartData.countData[i].count)
              .format('0.0 a')
              .toUpperCase()}
          </Td>
        </Tr>,
      )
    }
  }

  return (
    <Flex alignItems="center">
      <Box>
        {pieChartData && (
          <Doughnut data={pieChartData.data} options={pieChartData.options} />
        )}
      </Box>
      <Spacer />
      <Box>
        <Table>
          <Thead>
            <Tr>
              <Th>{t('type', 'Type')}</Th>
              <Th>{t('percentage', 'Percentage')}</Th>
              <Th>{t('requests', 'Requests')}</Th>
            </Tr>
          </Thead>
          <Tbody>{tableRows}</Tbody>
        </Table>
      </Box>
    </Flex>
  )
}

export default CacheHitRatio
